import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  installAxiosAuth,
  getStoredToken,
  getStoredUser,
  setAuthSession,
  clearAuthSession,
  formatApiErrorDetail,
} from "@/lib/api";
import { normalizeRole } from "@/lib/rbac";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeUser = (user) => {
  if (!user) return user;
  const role = normalizeRole(user.role);
  return {
    ...user,
    ...(role ? { role } : {}),
    individual_doctor: Boolean(user.individual_doctor),
  };
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = still checking auth, null = unauthenticated, object = authenticated
  const [user, setUser] = useState(undefined);
  const [permissions, setPermissions] = useState([]);
  // false until we get a definitive answer from /auth/me (or know there's no token).
  // Prevents PermissionRoute from redirecting before permissions are loaded.
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (_) {
      /* ignore */
    }
    clearAuthSession();
    setUser(null);
    setPermissions([]);
    setPermissionsLoaded(true);
  }, []);

  useEffect(() => {
    installAxiosAuth(() => {
      setUser(null);
      setPermissions([]);
      setPermissionsLoaded(true);
    });

    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setPermissionsLoaded(true);
      return;
    }

    // Optimistic user from cache for instant layout render
    const cached = getStoredUser();
    if (cached) setUser(normalizeUser(cached));

    axios
      .get(`${API}/auth/me`)
      .then((res) => {
        const nextUser = normalizeUser(res.data.user);
        setUser(nextUser);
        setPermissions(res.data.permissions || []);
        setAuthSession({ user: nextUser });
      })
      .catch(() => {
        clearAuthSession();
        setUser(null);
        setPermissions([]);
      })
      .finally(() => {
        setPermissionsLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      setAuthSession({
        access_token: res.data.access_token,
        refresh_token: res.data.refresh_token,
        user: normalizeUser(res.data.user),
      });
      const nextUser = normalizeUser(res.data.user);
      setUser(nextUser);

      let perms = [];
      try {
        const me = await axios.get(`${API}/auth/me`);
        perms = me.data.permissions || [];
        const meUser = normalizeUser(me.data.user) ?? nextUser;
        setUser(meUser);
        setPermissions(perms);
        setAuthSession({ user: meUser });
      } catch (_) {
        setPermissions([]);
      }
      setPermissionsLoaded(true);
      return { ok: true, permissions: perms, user: nextUser };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { ok: false, error: msg };
    }
  }, []);

  const hasPermission = useCallback(
    (perm) => permissions.includes(perm),
    [permissions]
  );
  const hasRole = useCallback(
    (...roles) => !!user && roles.includes(user.role),
    [user]
  );

  const value = {
    user,
    permissions,
    // isLoading is true until we know both the user AND their permissions.
    isLoading: user === undefined || !permissionsLoaded,
    isAuthenticated: !!user,
    signIn,
    signOut,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
